import { useState } from "react";
import { ButtonAsButton } from "@made-people/blueprint-button";
import { useTranslate } from "@made-people/grease-translations";
import type {
  ReturnsLookupItemV1,
  ReturnsLookupTableMappingV1,
} from "@made-people/nestjs-bff-service";
import type { Dispatch, FormEvent, MouseEvent, SetStateAction } from "react";
import { useBffClient } from "../../../../hooks/use-bff-client";
import { useFrontendContext } from "../../../../hooks/use-frontend-context";
import { getTableMappingIdForCountryCode } from "../../../../services/centra/mappers/utils/get-table-mapping-id-for-country-code";
import type { ReturnFlowState } from "./non-inretrn-return-flow";

type InitReturnFormProps = {
  setState: Dispatch<SetStateAction<ReturnFlowState>>;
  returnDaysLimit: number;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// email validation regex
const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const localizeAxis = (
  axis: ReturnsLookupTableMappingV1["x"],
  value: string | undefined,
): string | undefined => {
  if (!value || !axis || Array.isArray(axis)) {
    return undefined;
  }
  return axis[value];
};

const getLocalizedSize = (
  item: ReturnsLookupItemV1,
  countryCode: string,
): string => {
  const mapping =
    item.tableMappings?.[getTableMappingIdForCountryCode(countryCode)];
  if (!mapping) {
    return item.size;
  }

  const dividerSymbol = mapping.dividerSymbol ?? "";
  const [globalX, globalY] = dividerSymbol
    ? item.size.split(dividerSymbol)
    : [item.size, undefined];

  const localizedX = localizeAxis(mapping.x, globalX);
  const localizedY = localizeAxis(mapping.y, globalY);

  if (localizedX && localizedY) {
    return `${localizedX}${dividerSymbol}${localizedY}`;
  }

  return localizedX ?? item.size;
};

export const InitReturnForm = ({
  setState,
  returnDaysLimit,
}: InitReturnFormProps) => {
  const translate = useTranslate();
  const bffClient = useBffClient();
  const frontendContext = useFrontendContext();
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFormValid =
    orderNumber.trim().length > 0 && isValidEmail(email.trim());
  const isDisabled = isSubmitting || !isFormValid;
  //isFormValid is used to disable the button if the form is invalid

  const runLookup = async (form: HTMLFormElement) => {
    if (isDisabled || !form.reportValidity()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await bffClient.fetchReturnsLookup({
        orderNumber: orderNumber.trim(),
        email: email.trim(),
        language: frontendContext.language,
      });

      if (!response || response?.orderNumber !== orderNumber.trim()) {
        setError(translate("return_flow_lookup_error"));
        return;
      }
      if (response.deliveryCountry !== frontendContext.country) {
        setError(translate("return_flow_country_mismatch_error"));
        return;
      }

      const daysSinceOrder =
        (Date.now() - new Date(response.orderDate).getTime()) / MS_PER_DAY;
      if (daysSinceOrder > returnDaysLimit) {
        setError(translate("return_flow_return_period_expired_error"));
        return;
      }

      if (response?.shipments?.length > 0) {
        setError("");
        setState({
          status: "itemSelectionState",
          email: email.trim(),
          data: {
            ...response,
            items: response.items.map((item) => ({
              ...item,
              localSize: getLocalizedSize(item, response.deliveryCountry),
            })),
          },
        });
        return;
      } else {
        setError(translate("return_flow_no_shipments_error"));
        return;
      }
    } catch (lookupError) {
      console.error(lookupError);
      setError(translate("return_flow_lookup_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void runLookup(event.currentTarget);
  };

  const handleClick = (event?: MouseEvent<HTMLButtonElement>) => {
    const form = event?.currentTarget.form;
    if (form) {
      void runLookup(form);
    }
  };

  return (
    <div className="mt-component-spacing-3xl mb-component-spacing-5xl px-component-spacing-sm">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md mx-auto flex flex-col gap-4"
      >
        <label
          className={`relative block rounded-md border px-4 py-4 transition-colors ${
            error
              ? "border-system-error"
              : "border-gray-300 focus-within:border-gray-700"
          }`}
        >
          {error && (
            <span className="pointer-events-none absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-system-error">
              {translate("return_flow_form_orderNumber_label")} *
            </span>
          )}
          <input
            type="text"
            required
            aria-label="Order number"
            aria-invalid={error !== ""}
            placeholder={translate("return_flow_form_orderNumber_label")}
            value={orderNumber}
            onChange={(event) => {
              setOrderNumber(event.target.value);
              setError("");
            }}
            className="w-full appearance-none border-0 bg-transparent text-sm text-gray-700 outline-none focus:border-0 focus:outline-none focus:ring-0 placeholder:text-sm placeholder:text-gray-600"
          />
        </label>

        <label
          className={`relative block rounded-md border px-4 py-4 transition-colors ${
            error
              ? "border-system-error"
              : "border-gray-300 focus-within:border-gray-700"
          }`}
        >
          {error && (
            <span className="pointer-events-none absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-system-error">
              {translate("return_flow_form_email_label")} *
            </span>
          )}
          <input
            type="email"
            required
            aria-label="E-mail"
            aria-invalid={error !== ""}
            placeholder={translate("return_flow_form_email_label")}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setError("");
            }}
            className="w-full appearance-none border-0 bg-transparent text-sm text-gray-700 outline-none focus:border-0 focus:outline-none focus:ring-0 placeholder:text-sm placeholder:text-gray-600"
          />
        </label>

        <div className="w-full">
          <ButtonAsButton
            label={translate("return_flow_form_submit_button_text")}
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            progress={isSubmitting ? "loading" : "default"}
            variants={{ width: "full", rounded: "md", color: "black" }}
          />
        </div>
        {error && (
          <p
            className="text-xs text-system-error"
            role="alert"
            aria-live="polite"
          >
            {error}
          </p>
        )}
      </form>
    </div>
  );
};
